/** Runtime config from config.js. Required; throws if missing. */
export interface UnpispasRuntimeConfig {
    domain?: string;

    appPath?: string;
    apiPath?: string;
    wsrPath?: string;
    qraPath?: string;

    appRoute?: string;
    apiRoute?: string;
    wsrRoute?: string;
    qraRoute?: string;

    production?: boolean;
    appVersion?: string;
    ticketbai_sandbox?: boolean;
    verifactu_sandbox?: boolean;
    googleAnalytics?: string;
}

export class AppConstants {

    /********************************************/
    /* STATIC VALUES (CONSTANTS)                */
    /********************************************/

    public static get TicketBaiSandbox(): boolean { return this.Config().ticketbai_sandbox ?? true; }
    public static get VeriFactuSandbox(): boolean { return this.Config().verifactu_sandbox ?? true; }
    
    public static get SoftwareDeveloperName(): string { return 'GEKKOTECH SL'; }
    public static get SoftwareDeveloperTaxId(): string { return 'B70659198'; }
    public static get SoftwareDeveloperFiscal(): string { return 'Calle Basañese 4, 48940 Leioa, Bizkaia'; }
    public static get SoftwareSystemId(): string { return 'PS'; }

    private static get TicketBaiRegistry(): any {
        return {
            PRD: {
                'Bizkaia': {
                    license: 'TBAIBIjWPDMtTEyW1aA+',
                    appname: 'Unpispas',
                    developer: AppConstants.SoftwareDeveloperName,
                    nif: AppConstants.SoftwareDeveloperTaxId
                },
                'Gipuzkoa': {
                    license: 'TBAIGI3A40424979E2F0',
                    appname: 'Unpispas',
                    developer: AppConstants.SoftwareDeveloperName,
                    nif: AppConstants.SoftwareDeveloperTaxId
                },
                'Araba': {
                    license: 'TBAIARCdjdCgKHF00204',
                    appname: 'Unpispas',
                    developer: AppConstants.SoftwareDeveloperName,
                    nif: AppConstants.SoftwareDeveloperTaxId
                }
            },
            DEV: {
                'Bizkaia': {
                    license: 'TBAIBI00000000PRUEBA',
                    appname: 'UnPisPas',
                    developer: 'SOFTWARE GARANTE TICKETBAI PRUEBA',
                    nif: 'A99800005'                    
                },
                'Gipuzkoa': {
                    license: 'TBAIGIPRE00000000988',
                    appname: 'Unpispas www.unpispas.com',
                    developer: 'Jorge Alejando Duran Royo, ATM tiendas y mulsitervicio SLu',
                    nif: 'B99278319'
                },
                'Araba': {
                    license: 'TBAIARCjHMggMlH00868',
                    appname: 'Unpispas',
                    developer: 'Tienda y multiservicios ATM S.L',
                    nif: 'B99278319'     
                }
            }
        }
    }

    public static get isProduction(): boolean { return this.Config().production ?? false; }
    public static get Appname(): string { return 'UnPisPas' }
    public static get Version(): string { return this.Config().appVersion ?? '0.0.0.0'; }
    public static get Build(): string { return "0" }    // obsoleted when managing with nx

    public static get TaxRate1(): number { return 4; }
    public static get TaxRate2(): number { return 10; }
    public static get TaxRate3(): number { return 21; }

    public static get CacheExpiration(): number { return 5 * 24 * 3600 * 1000 }
    public static get recentMs(): number { return 8 * 3600 * 1000; }
    public static get defaultTaxRate(): number { return this.TaxRate2; }
    public static get invoiceMaxPrice(): number { return 3000; }
    public static get defaultPrintWidth(): number { return 58; }
    public static get defaultPrintFont(): number { return 12; }
    public static get thumbnailWidth(): number { return 140; };
    public static get thumbnailColumns(): number { return 24; }
    public static get SearchThreshold(): number { return 80; }

    public static TicketBaiAppname(region: string) : string { return AppConstants.TicketBaiRegistry[AppConstants.TicketBaiSandbox ? 'DEV' : 'PRD'][region]['appname']; }
    public static TicketBaiAppvers(region: string) : string { return region ? AppConstants.Version : '0.0.0.0'; }
    public static TicketBaiLicense(region: string): string { return AppConstants.TicketBaiRegistry[AppConstants.TicketBaiSandbox ? 'DEV' : 'PRD'][region]['license']; }
    public static TicketBaiDeveloper(region: string): string { return AppConstants.TicketBaiRegistry[AppConstants.TicketBaiSandbox ? 'DEV' : 'PRD'][region]['developer']; }
    public static TicketBaiNIF(region: any): string { return AppConstants.TicketBaiRegistry[AppConstants.TicketBaiSandbox ? 'DEV' : 'PRD'][region]['nif']; }

    public static get defaultLanguage(): string { return 'es'; }
    public static get MainRefresh(): number { return 500; } // ms

    /** Runtime config from config.js. Throws if missing. */
    private static Config(): UnpispasRuntimeConfig {
        if (typeof window === 'undefined') {
            throw new Error('__UNPISPAS_CONFIG__: config.js required but window is undefined (e.g. Node/test). Set (globalThis as any).window = { __UNPISPAS_CONFIG__: {...} } in test setup.');
        }
        const c = (window as unknown as { __UNPISPAS_CONFIG__?: UnpispasRuntimeConfig }).__UNPISPAS_CONFIG__;
        if (!c || typeof c !== 'object') {
            throw new Error('__UNPISPAS_CONFIG__: config.js required but not loaded. Ensure <script src="config.js"></script> runs before Angular.');
        }
        if (c.appPath == null || c.apiPath == null || c.wsrPath == null || c.domain == null) {
            throw new Error('__UNPISPAS_CONFIG__: config.js must define appPath, apiPath, wsrPath and domain.');
        }
        return c;
    }

    public static get apppath(): string {
        const appPath = this.Config().appPath;
        if (appPath == null) {
            throw new Error('__UNPISPAS_CONFIG__: appPath is required.');
        }
        return appPath;
    }

    /**
     * Public path of the SPA that loaded this `config.js` (`appRoute`).
     * Distinct from {@link qraURL} path, which is the guest product URL even when POS is running.
     */
    public static get appRoute(): string {
        return this.Config().appRoute ?? '/';
    }
    
    public static get domain(): string {
        const domain = this.Config().domain;
        if (domain == null) {
            throw new Error('__UNPISPAS_CONFIG__: domain is required.');
        }
        return domain;
    }

    public static get baseURL(): string {
        const c = this.Config();
        return 'https://' + c.apiPath + c.domain + (c.apiRoute ?? '/');
    }

    public static get thisURL(): string {
        const c = this.Config();
        return 'https://' + c.appPath + c.domain + (c.appRoute ?? '/');
    }

    /**
     * Guest QR SPA public base. Same role as PHP `AppConstants::QraUrl()`.
     * Distinct from {@link thisURL} (POS / current app). Used for table stickers.
     */
    public static get qraURL(): string {
        const c = this.Config();
        if (c.qraPath == null) {
            throw new Error('__UNPISPAS_CONFIG__: qraPath is required.');
        }
        return 'https://' + c.qraPath + c.domain + (c.qraRoute ?? '/');
    }
    
    /**
     * Device WSR REST relay base URL (`POST /send?service=…`).
     * Distinct from {@link baseURL} (PHP Slim API): same host family, `wsr*` subdomain.
     */
    public static get restURL(): string {
        const c = this.Config();
        return 'https://' + c.wsrPath + c.domain + (c.wsrRoute ?? '/');
    }
  
    public static get uploadPath(): string { return 'upload/'; }
    public static get googleApiKey(): string { return 'AIzaSyDLbnVeXUMsJEu6CtgjM5s92ht5xji0Ulk'; }
    public static get GoogleMapKey(): string { return '989754db8ad5636d' }
    public static get googleOAuth2Key(): string { return '404259737376-8t1cgibk48q5jji5sb8b8nc1b5a726hs.apps.googleusercontent.com'; }
    public static get GoogleAnalitics(): string { return this.Config().googleAnalytics ?? ''; }    
    public static get FCMVapidKey(): string { return 'BOZ-hqmu5VZKdrjUC06uqqmheRjM4zv4CWZXeahLUcXONW8B1s-DeZXf-2AJ0JqtwWuoK6lJAo0f2K-Ks-HvI3A'; };

    /**
     * Public Firebase web config (D-SECREM-03). Same values as `firebase-messaging-sw.js`.
     */
    public static get FirebaseConfig(): {
        apiKey: string;
        authDomain: string;
        projectId: string;
        storageBucket: string;
        messagingSenderId: string;
        appId: string;
        measurementId: string;
    } {
        return {
            apiKey: 'AIzaSyBP2r2gQXRGSXkKCDqqcsO3lpqfKTijdiM',
            authDomain: 'unpispas-415412.firebaseapp.com',
            projectId: 'unpispas-415412',
            storageBucket: 'unpispas-415412.appspot.com',
            messagingSenderId: '185144898799',
            appId: '1:185144898799:web:e465b31a37d004b0b3171a',
            measurementId: 'G-0K88WNP5YV',
        };
    }
}
